import { useState, useEffect } from 'react';
import { useLocation, useParams } from 'wouter';
import { Header } from '@/components/layout/Header';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { TagBadge } from '@/components/shared/TagBadge';
import { useInitWedding } from '@/hooks/useWedding';
import { useEvents, useCreateEvent, useUpdateEvent, useDeleteEvent } from '@/hooks/useEvents';
import { useToast } from '@/hooks/useToast';
import { ALL_CATEGORIES, CATEGORY_LABELS } from '@/lib/constants';
import type { EventCategory } from '@/lib/types';
import { CheckCircle } from 'lucide-react';

interface FormState {
  name: string;
  date: string;
  startTime: string;
  endTime: string;
  category: EventCategory;
  location: string;
  notes: string;
}

interface FormErrors {
  name?: string;
  endTime?: string;
}

function combineDatetime(date: string, time: string): string {
  // Returns ISO string like "2024-06-15T14:30:00Z"
  if (!date || !time) return '';
  return new Date(`${date}T${time}`).toISOString();
}

function splitDatetime(iso: string): { date: string; time: string } {
  if (!iso) return { date: '', time: '' };
  const d = new Date(iso);
  const date = d.toLocaleDateString('en-CA'); // YYYY-MM-DD
  const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }); // HH:MM
  return { date, time };
}

export function EventFormPage() {
  const [, navigate] = useLocation();
  const params = useParams<{ id?: string }>();
  const eventId = params.id;
  const isEdit = !!eventId;

  const { weddingId } = useInitWedding();
  const { data: events } = useEvents(weddingId);
  const createEvent = useCreateEvent(weddingId ?? '');
  const updateEvent = useUpdateEvent(weddingId ?? '');
  const deleteEvent = useDeleteEvent(weddingId ?? '');
  const { toast } = useToast();

  const today = new Date().toLocaleDateString('en-CA');
  const [form, setForm] = useState<FormState>({
    name: '',
    date: today,
    startTime: '10:00',
    endTime: '11:00',
    category: 'all',
    location: '',
    notes: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitted, setSubmitted] = useState(false);
  const [categorySheetOpen, setCategorySheetOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Pre-fill form in edit mode
  useEffect(() => {
    if (isEdit && events) {
      const existing = events.find((e) => e.id === eventId);
      if (existing) {
        const { date, time: startT } = splitDatetime(existing.startTime);
        const { time: endT } = splitDatetime(existing.endTime);
        setForm({
          name: existing.name,
          date,
          startTime: startT,
          endTime: endT,
          category: existing.category,
          location: existing.location ?? '',
          notes: existing.notes ?? '',
        });
      }
    }
  }, [isEdit, eventId, events]);

  function validate(): FormErrors {
    const errs: FormErrors = {};
    if (!form.name.trim()) errs.name = 'Event name is required';
    if (form.date && form.startTime && form.endTime) {
      const start = new Date(`${form.date}T${form.startTime}`);
      const end = new Date(`${form.date}T${form.endTime}`);
      if (end <= start) errs.endTime = 'End time must be after start time';
    }
    return errs;
  }

  function handleSave() {
    setSubmitted(true);
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setIsSaving(true);
    const startIso = combineDatetime(form.date, form.startTime);
    const endIso = combineDatetime(form.date, form.endTime);

    const payload = {
      name: form.name.trim(),
      startTime: startIso,
      endTime: endIso,
      category: form.category,
      location: form.location.trim() || null,
      notes: form.notes.trim() || null,
      notify: true,
      status: 'upcoming' as const,
      sortOrder: 0,
    };

    if (isEdit && eventId) {
      updateEvent.mutate({ eventId, data: payload }, {
        onSuccess: () => { toast('Event updated', 'success'); navigate('/schedule'); },
        onError: () => { toast('Failed to update event', 'error'); setIsSaving(false); },
      });
    } else {
      createEvent.mutate(payload, {
        onSuccess: () => { toast('Event added', 'success'); navigate('/schedule'); },
        onError: () => { toast('Failed to add event', 'error'); setIsSaving(false); },
      });
    }
  }

  function handleDelete() {
    if (!eventId) return;
    deleteEvent.mutate(eventId, {
      onSuccess: () => { toast('Event deleted', 'success'); navigate('/schedule'); },
      onError: () => toast('Failed to delete event', 'error'),
    });
  }

  const inputStyle: React.CSSProperties = {
    width: '100%',
    background: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-md)',
    color: 'var(--color-text)',
    padding: '12px 14px',
    fontSize: '15px',
    fontFamily: 'var(--font-body)',
    outline: 'none',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '13px',
    fontWeight: 500,
    color: 'var(--color-text-secondary)',
    marginBottom: '6px',
  };

  return (
    <div className="flex flex-col min-h-screen" style={{ background: 'var(--color-background)' }}>
      <Header
        title={isEdit ? 'Edit Event' : 'Add Event'}
        backHref="/schedule"
      />

      <main className="flex-1 px-4 py-4 pb-32 space-y-5">
        {/* Name */}
        <div>
          <label style={labelStyle}>Event Name *</label>
          <input
            style={{
              ...inputStyle,
              borderColor: submitted && errors.name ? 'var(--color-error)' : 'var(--color-border)',
            }}
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="e.g. Ceremony begins"
            autoFocus
          />
          {submitted && errors.name && (
            <p style={{ color: 'var(--color-error)', fontSize: '12px', marginTop: '4px' }}>{errors.name}</p>
          )}
        </div>

        {/* Date */}
        <div>
          <label style={labelStyle}>Date</label>
          <input
            type="date"
            style={inputStyle}
            value={form.date}
            onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
          />
        </div>

        {/* Start + End Time */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label style={labelStyle}>Start Time</label>
            <input
              type="time"
              style={inputStyle}
              value={form.startTime}
              onChange={(e) => setForm((f) => ({ ...f, startTime: e.target.value }))}
            />
          </div>
          <div>
            <label style={labelStyle}>End Time</label>
            <input
              type="time"
              style={{
                ...inputStyle,
                borderColor: submitted && errors.endTime ? 'var(--color-error)' : 'var(--color-border)',
              }}
              value={form.endTime}
              onChange={(e) => setForm((f) => ({ ...f, endTime: e.target.value }))}
            />
            {submitted && errors.endTime && (
              <p style={{ color: 'var(--color-error)', fontSize: '12px', marginTop: '4px' }}>{errors.endTime}</p>
            )}
          </div>
        </div>

        {/* Category */}
        <div>
          <label style={labelStyle}>Category</label>
          <button
            style={{
              ...inputStyle,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
            onClick={() => setCategorySheetOpen(true)}
            type="button"
          >
            <TagBadge category={form.category} />
            <span style={{ color: 'var(--color-text-secondary)', fontSize: '13px' }}>Change ›</span>
          </button>
        </div>

        {/* Location */}
        <div>
          <label style={labelStyle}>Location (optional)</label>
          <input
            style={inputStyle}
            value={form.location}
            onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
            placeholder="e.g. Garden Terrace"
          />
        </div>

        {/* Notes */}
        <div>
          <label style={labelStyle}>Notes (optional)</label>
          <textarea
            style={{ ...inputStyle, resize: 'vertical', minHeight: '72px' }}
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            placeholder="Any additional details..."
            rows={3}
          />
        </div>
      </main>

      {/* Bottom action area */}
      <div
        className="fixed bottom-0 left-0 right-0 px-4 pt-3 pb-safe space-y-2 max-w-lg mx-auto"
        style={{ background: 'var(--color-background)', borderTop: '1px solid var(--color-border)' }}
      >
        {isEdit && (
          <button
            className="w-full text-sm font-medium py-2 touch-target"
            style={{ color: 'var(--color-error)' }}
            onClick={() => setDeleteConfirmOpen(true)}
            type="button"
          >
            Delete Event
          </button>
        )}
        <button
          className="w-full py-3 rounded-xl text-sm font-semibold touch-target"
          style={{
            background: isSaving ? 'var(--color-primary-muted)' : 'var(--color-primary)',
            color: '#1A1A2E',
          }}
          onClick={handleSave}
          disabled={isSaving}
          type="button"
        >
          {isSaving ? 'Saving…' : 'Save Event'}
        </button>
      </div>

      {/* Category picker bottom sheet */}
      {categorySheetOpen && (
        <>
          <div
            className="fixed inset-0 z-50"
            style={{ background: 'rgba(0,0,0,0.7)' }}
            onClick={() => setCategorySheetOpen(false)}
          />
          <div
            className="fixed bottom-0 left-0 right-0 z-50 rounded-t-2xl pb-safe max-w-lg mx-auto animate-slide-up"
            style={{ background: 'var(--color-surface)' }}
          >
            <div className="flex justify-center pt-3 pb-2">
              <div className="w-10 h-1 rounded-full" style={{ background: 'var(--color-border)' }} />
            </div>
            <p className="text-center text-sm font-semibold pb-3" style={{ color: 'var(--color-text)' }}>
              Select Category
            </p>
            {ALL_CATEGORIES.map((cat) => (
              <button
                key={cat}
                className="w-full flex items-center justify-between px-5 touch-target"
                style={{ minHeight: '48px' }}
                onClick={() => {
                  setForm((f) => ({ ...f, category: cat }));
                  setCategorySheetOpen(false);
                }}
                type="button"
              >
                <TagBadge category={cat} />
                <span style={{ fontSize: '13px', color: 'var(--color-text-secondary)' }}>
                  {CATEGORY_LABELS[cat]}
                </span>
                {form.category === cat && (
                  <CheckCircle size={16} style={{ color: 'var(--color-primary)' }} />
                )}
              </button>
            ))}
            <div className="pb-4" />
          </div>
        </>
      )}

      <ConfirmDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title="Delete Event"
        description="This will permanently remove this event from your timeline."
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={handleDelete}
      />
    </div>
  );
}
